import { TeamMembersState, State } from '../configure-store';
import { ContributorStatusType } from '../../common/types';
import { InviteData, UserData, TeamData } from '../../types/teamMembers';
import { getIsSite } from './config';
import { isOwner } from './roleAreas';

export const getTeamMembersList = (state: State): TeamMembersState => state.teamMembers;

export const getContributorById = (state: State, id: string): InviteData | UserData => state.teamMembers.users.find(user => user.id === id) ||
  state.teamMembers.invites.find(invite => invite.id === id);

export const getUserOrInviteById = (state: State, id: string): UserData | InviteData | TeamData => {
  return getIsUser(state, id) ? getUserById(state, id) : (getIsInvite(state, id) ? getInviteById(state, id) : getTeamById(state, id));
};

export const getUserById = (state: State, id: string): UserData =>
  state.teamMembers.users.find(user => user.id === id);

export const getInviteById = (state: State, id: string): InviteData =>
  state.teamMembers.invites.find(invite => invite.id === id);

export const getTeamById = (state: State, id: string): TeamData =>
  state.teamMembers.teams.find(team => team.id === id);

export const getInviteByLink = (state: State, link: string): InviteData =>
  state.teamMembers.invites.find(invite => invite.acceptLink === link);

export const getContributorEmailById = (state: State, id: string): string => {
  const contributor = getContributorById(state, id);
  return contributor && contributor.email;
};

export const getAllContributorEmails = (state: State): string[] => (
  state.teamMembers.users.concat(state.teamMembers.invites as any).map(user => user.email)
);

export const getContributorStatus = (state: State, id: string): ContributorStatusType => {
  if (getUserById(state, id)) {
    return ContributorStatusType.USER;
  }
  if (getInviteById(state, id)) {
    return ContributorStatusType.INVITE;
  }
  if (getTeamById(state, id)) {
    return ContributorStatusType.TEAM;
  }
  return null;
};

export const getUserRoles = (state: State, userId: string): string[] => {
  const user = getUserById(state, userId);
  if (!user) { return []; }
  return user.roles;
};

export const getInviteRoles = (state: State, inviteId: string): string[] => {
  const invite = getInviteById(state, inviteId);
  if (!invite) {
    return [];
  } else {
    return invite.roles;
  }
};

export const getIsUser = (state: State, id: string): boolean =>  getContributorStatus(state, id) === ContributorStatusType.USER;
export const getIsInvite = (state: State, id: string): boolean =>  getContributorStatus(state, id) === ContributorStatusType.INVITE;

export const DEFAULT_USERS_LIMIT = 6;

//TODO: userPagination, should be removed
const shouldUseCustomUserLimit = (state: State): boolean => {
  const isSite = getIsSite(state);
  if (!isSite && state.debug) {
    const urlParams = new URLSearchParams(window.location.search);
    const usersLimit = urlParams.get('usersLimit');
    if (usersLimit && !isNaN(+usersLimit)) {
      return true;
    }
  }
  return false;
};

export const getUsersLimit = (state: State): number => {
  if (shouldUseCustomUserLimit(state)) {
    const urlParams = new URLSearchParams(window.location.search);
    const usersLimit = urlParams.get('usersLimit');
    return +usersLimit;
  } else {
    return DEFAULT_USERS_LIMIT;
  }
};

export const getNumberOfUserPages = (userLimit: number, totalUsersInAccount: string | undefined): number => {
  const totalUsersInAccountNumber = +totalUsersInAccount;
  if (isNaN(totalUsersInAccountNumber) || (totalUsersInAccountNumber < userLimit)) {
    return -1;
  } else {
    return Math.ceil((totalUsersInAccountNumber - userLimit) / userLimit);
  }
};

export const getInvites = (state: State): InviteData[] => state.teamMembers?.invites ?? [];

export const getUsers = (state: State): UserData[] => state.teamMembers?.users ?? [];

export const getTeams = (state: State): TeamData[] => state.teamMembers?.teams ?? [];

export const getNumberOfContributors = (state: State): number => {
  const invites = getInvites(state);
  const users = getUsers(state);
  const teams = getTeams(state);
  return invites.length + users.length + teams.length;
};

export const getOwnerId = (state: State): string => {
  const { teamMembers: { users = [], teams = [] } } = state;
  const teamMembersMerged = [...users, ...teams];
  const owner = teamMembersMerged.find((teamMember) => teamMember.roles.some(role => isOwner(state, role)));
  return owner?.id;
};
